import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert } from '@mui/material'
import Pagination from '../../layout/Pagination'
import usePagination from '../../layout/usePagination'
import LoadingScreen from '../../components/LoadingScreen'
import { canManageSpecialPrices } from '../../global_context/capabilities/priceCapabilities'
import AlertErrorMessage from '../price_list/helpers/AlertErrorMessage'
import SpecialPriceFilters from './filter/SpecialPriceFilters'
import SpecialPriceIndexTable from './components/SpecialPriceIndexTable'
import {
  SPECIAL_PRICE_DEFAULT_PER_PAGE,
  SPECIAL_PRICE_FILTERS_INITIAL_STATE
} from './constant/specialPriceViewConstants'
import {
  fetchSpecialPriceFilterInfo as fetchSpecialPriceFilterInfoRequest,
  fetchSpecialPrices as fetchSpecialPricesRequest,
  suspendSpecialPrice
} from './helpers/specialPriceApi'
import {
  extractErrorMessage
} from './helpers/specialPriceHelpers'
import {
  areFiltersEqual,
  buildSearchQuery,
  buildRequestQuery,
  buildSpecialPriceFilterOptions,
  getSpecialPriceLocationState,
  hasActiveFilters,
  normalizeFilterInfoResponse,
  toFilterSelectValue
} from './helpers/specialPriceIndexHelpers'
import '../../css/special_price.scss'

const SpecialPriceIndexView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [specialPrices, setSpecialPrices] = useState([])
  const [filters, setFilters] = useState(() => getSpecialPriceLocationState(window.location.search).filters)
  const [searchInput, setSearchInput] = useState(
    () => getSpecialPriceLocationState(window.location.search).searchInput
  )
  const [filterInfo, setFilterInfo] = useState(null)
  const [isFetching, setIsFetching] = useState(true)
  const [isFetchingFilters, setIsFetchingFilters] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [flashMessage, setFlashMessage] = useState(location.state?.successMessage || '')
  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: SPECIAL_PRICE_DEFAULT_PER_PAGE,
    current_page: 1,
    total_pages: 1
  })
  const { current_page: currentPage, per_page: perPage } = pagination
  const canWrite = canManageSpecialPrices()
  const isLoading = isFetching || isFetchingFilters
  const shouldShowReset = useMemo(() => hasActiveFilters(filters, searchInput), [filters, searchInput])
  const filterOptions = useMemo(
    () => buildSpecialPriceFilterOptions(filterInfo),
    [filterInfo]
  )

  const syncUrl = useCallback(
    (nextFilters, nextSearchInput, nextPagination) => {
      const query = buildSearchQuery({
        filters: nextFilters,
        searchInput: nextSearchInput,
        pagination: nextPagination
      })
      const currentSearch = window.location.search.replace(/^\?/, '')

      if (currentSearch === query) return

      navigate(
        {
          pathname: location.pathname,
          search: query ? `?${query}` : ''
        },
        { replace: true, state: location.state }
      )
    },
    [location.pathname, location.state, navigate]
  )

  const fetchSpecialPrices = useCallback(async () => {
    setIsFetching(true)
    setErrorMessage('')

    try {
      const data = await fetchSpecialPricesRequest(
        buildRequestQuery({
          filters,
          pagination: {
            current_page: currentPage,
            per_page: perPage
          }
        })
      )

      setSpecialPrices(data.table || [])
      setPagination((prev) => {
        const nextTotalPages = Math.max(1, Math.ceil((data.count || 0) / prev.per_page))

        if (prev.total_pages === nextTotalPages) {
          return prev
        }

        return {
          ...prev,
          total_pages: nextTotalPages
        }
      })
    } catch (error) {
      console.error(error)
      setErrorMessage('No se pudieron cargar los precios especiales.')
      setSpecialPrices([])
    } finally {
      setIsFetching(false)
    }
  }, [currentPage, filters, perPage, setPagination])

  const fetchFilterInfo = useCallback(async () => {
    setIsFetchingFilters(true)

    try {
      const data = await fetchSpecialPriceFilterInfoRequest()
      setFilterInfo(normalizeFilterInfoResponse(data))
    } catch (error) {
      console.error(error)
      setFilterInfo({})
    } finally {
      setIsFetchingFilters(false)
    }
  }, [])

  useEffect(() => {
    fetchFilterInfo()
  }, [fetchFilterInfo])

  useEffect(() => {
    fetchSpecialPrices()
  }, [fetchSpecialPrices])

  useEffect(() => {
    const locationState = getSpecialPriceLocationState(location.search, pagination.per_page)
    const filtersFromSearch = locationState.filters
    const nextSearchInput = locationState.searchInput
    const nextPage = locationState.currentPage
    const nextPerPage = locationState.perPage

    if (!areFiltersEqual(filters, filtersFromSearch)) {
      setFilters(filtersFromSearch)
    }

    if (searchInput !== nextSearchInput) {
      setSearchInput(nextSearchInput)
    }

    if (pagination.current_page !== nextPage || pagination.per_page !== nextPerPage) {
      setPagination((prev) => ({
        ...prev,
        current_page: nextPage,
        per_page: nextPerPage
      }))
    }
  }, [location.search, setPagination])

  const showModel = (specialPriceId) => {
    navigate(`/precios_especiales/${specialPriceId}`)
  }

  const handleFilterChange = (field, value) => {
    const nextPagination = { ...pagination, current_page: 1 }
    const nextFilters = {
      ...filters,
      [field]: value
    }

    setPagination((prev) => ({ ...prev, current_page: 1 }))
    setFilters(nextFilters)
    syncUrl(nextFilters, searchInput, nextPagination)
  }

  const handleMultiFilterChange = (field) => (value) => {
    handleFilterChange(field, value?.mode === 'custom' ? value.values || [] : [])
  }

  const resetFilters = () => {
    const nextFilters = { ...SPECIAL_PRICE_FILTERS_INITIAL_STATE }
    const nextPagination = { ...pagination, current_page: 1 }

    setSearchInput('')
    setFilters(nextFilters)
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    syncUrl(nextFilters, '', nextPagination)
  }

  const handleSearchEnter = (event) => {
    if (event.key !== 'Enter') return

    event.preventDefault()
    const nextFilters = {
      ...filters,
      q: searchInput
    }
    const nextPagination = { ...pagination, current_page: 1 }

    setFilters(nextFilters)
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    syncUrl(nextFilters, searchInput, nextPagination)
  }

  const handleClearSearch = () => {
    const nextFilters = {
      ...filters,
      q: ''
    }
    const nextPagination = { ...pagination, current_page: 1 }

    setSearchInput('')
    setFilters(nextFilters)
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    syncUrl(nextFilters, '', nextPagination)
  }

  const handleSuspend = async (specialPrice) => {
    const confirmed = window.confirm('¿Está seguro de que desea suspender este precio especial?')

    if (!confirmed) return

    try {
      const response = await suspendSpecialPrice(specialPrice.uid)

      if (!response?.ok && response?.status !== 204) {
        throw new Error(
          extractErrorMessage(response?.data, 'No se pudo suspender el precio especial.')
        )
      }

      fetchSpecialPrices()
    } catch (error) {
      setErrorMessage(error.message || 'No se pudo suspender el precio especial.')
    }
  }

  const handleCopy = (specialPrice) => {
    navigate(`/crear_precio_especial?copy=${specialPrice.uid}`)
  }

  const openProductDetails = (event, productId) => {
    event.preventDefault()
    event.stopPropagation()
    if (!productId) return
    navigate(`/productos/${productId}/detalles`)
  }

  useEffect(() => {
    syncUrl(filters, searchInput, pagination)
  }, [pagination.current_page, pagination.per_page, syncUrl])

  if (isLoading && !filterInfo && specialPrices.length === 0) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <>
      {errorMessage && (
        <AlertErrorMessage message={errorMessage} onClose={() => setErrorMessage('')} />
      )}
      {flashMessage && (
        <Alert
          severity="success"
          onClose={() => setFlashMessage('')}
          sx={{
            mb: 2,
            '& .MuiAlert-icon': {
              marginLeft: 2
            }
          }}
        >
          {flashMessage}
        </Alert>
      )}

      <div className="index-header detailed-container">
        <h2 className="mb-20">Precios Especiales</h2>
        <div className="index-buttons">
          {canWrite && (
            <button onClick={() => navigate('/crear_precio_especial')}>
              Crear precio especial
            </button>
          )}
        </div>
      </div>

      <div className="index-table">
        <div className="show-area">
          <SpecialPriceFilters
            clientAttributeFilter={toFilterSelectValue(filters.client_attribute)}
            productFilter={toFilterSelectValue(filters.product)}
            salesUnitFilter={toFilterSelectValue(filters.sales_unit)}
            statusFilter={toFilterSelectValue(filters.status)}
            clientAttributeOptions={filterOptions.client_attribute}
            productOptions={filterOptions.product}
            salesUnitOptions={filterOptions.sales_unit}
            statusOptions={filterOptions.status}
            searchInput={searchInput}
            dateRange={filters.date_range}
            onSearchChange={(event) => setSearchInput(event.target.value)}
            onSearchEnter={handleSearchEnter}
            onClearSearch={handleClearSearch}
            onClientAttributeChange={handleMultiFilterChange('client_attribute')}
            onProductChange={handleMultiFilterChange('product')}
            onSalesUnitChange={handleMultiFilterChange('sales_unit')}
            onStatusChange={handleMultiFilterChange('status')}
            onDateRangeChange={(value) => handleFilterChange('date_range', value)}
            onReset={resetFilters}
            shouldShowReset={shouldShowReset}
            isLoading={isLoading}
          />

          <SpecialPriceIndexTable
            canWrite={canWrite}
            specialPrices={specialPrices}
            onCopy={handleCopy}
            onOpenProductDetails={openProductDetails}
            onRowClick={showModel}
            onSuspend={handleSuspend}
          />
        </div>
      </div>

      {pagination.total_pages > 1 && (
        <nav className="index-table">
          <Pagination
            current_page={pagination.current_page}
            total_pages={pagination.total_pages}
            handle_pagination={handlePaginationClick}
          />
        </nav>
      )}
    </>
  )
}

export default SpecialPriceIndexView
